import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Input,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Switch,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react";
import { ChevronDownIcon, RepeatIcon } from "@chakra-ui/icons";
import { Circle, CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, useMap } from "react-leaflet";
import { divIcon } from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaFireAlt, FaMapMarkerAlt, FaRoute, FaVideo } from "react-icons/fa";

const DEFAULT_BASEMAP_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

const PERIOD_OPTIONS = [
  { value: "1", label: "Última hora" },
  { value: "6", label: "Últimas 6 horas" },
  { value: "24", label: "Últimas 24 horas" },
  { value: "168", label: "Últimos 7 dias" }
];

const VEHICLE_TYPE_OPTIONS = ["CARRO", "MOTO", "ÔNIBUS", "CAMINHÃO", "BICICLETA", "DESCONHECIDO"];

const formatNumber = (value) => Number(value || 0).toLocaleString("pt-BR");

const formatDateTime = (value) => {
  if (!value) return "Sem registro";
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getTrafficColor = (intensity) => {
  if (intensity >= 0.75) return "#dc2626";
  if (intensity >= 0.45) return "#f97316";
  if (intensity >= 0.2) return "#facc15";
  return "#14b8a6";
};

const getFlowWeight = (intensity, zoomFactor = 1) => Math.max(2, Math.round((3 + intensity * 12) * zoomFactor));
const HEATMAP_MIN_RADIUS_METERS = 25;
const HEATMAP_MAX_RADIUS_METERS = 450;

const ONLINE_THRESHOLD_MS = 15 * 60 * 1000;
const isCameraOnline = (lastDetectedAt) =>
  !!lastDetectedAt && Date.now() - new Date(lastDetectedAt).getTime() < ONLINE_THRESHOLD_MS;

const makeCameraIcon = (online) => {
  const dot = online ? "#22c55e" : "#ef4444";
  return divIcon({
    className: "",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    popupAnchor: [0, -14],
    html: `<div style="position:relative;display:inline-block;width:22px;height:22px;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22"><rect width="24" height="24" rx="5" fill="#2d3748"/><path fill="white" d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z"/></svg><span style="position:absolute;top:-3px;right:-3px;width:9px;height:9px;border-radius:50%;background:${dot};border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.5);"></span></div>`
  });
};

const CAMERA_ICON_ONLINE = makeCameraIcon(true);
const CAMERA_ICON_OFFLINE = makeCameraIcon(false);

const ZoomAwareFlows = ({ flows }) => {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());

  useEffect(() => {
    const handler = () => setZoom(map.getZoom());
    map.on("zoomend", handler);
    return () => map.off("zoomend", handler);
  }, [map]);

  // zoomFactor: 0.15 when zoomed out (zoom<=10), up to 1.0 when zoomed in (zoom>=16)
  const zoomFactor = Math.max(0.15, Math.min(1, (zoom - 10) / 6));

  return (
    <>
      {flows.flatMap((flow) =>
        flow.segments.flatMap((segment, segIdx) => {
          const positions = segment.positions || segment;
          const intensity = segment.intensity ?? flow.intensity;
          return [
            <Polyline
              key={`flow-${flow.id}-${segIdx}-halo`}
              positions={positions}
              pathOptions={{
                color: getTrafficColor(intensity),
                weight: getFlowWeight(intensity, zoomFactor) + 4,
                opacity: 0.24,
                lineCap: "round",
                lineJoin: "round"
              }}
            />,
            <Polyline
              key={`flow-${flow.id}-${segIdx}`}
              positions={positions}
              pathOptions={{
                color: getTrafficColor(intensity),
                weight: getFlowWeight(intensity, zoomFactor),
                opacity: 0.78,
                lineCap: "round",
                lineJoin: "round"
              }}
            >
              <Popup><FlowPopup flow={flow} /></Popup>
            </Polyline>
          ];
        })
      )}
    </>
  );
};

const FitMapBounds = ({ cameras }) => {
  const map = useMap();
  const boundsKey = cameras.map((camera) => `${camera.latitude},${camera.longitude}`).join("|");

  useEffect(() => {
    if (!cameras.length) return;
    const bounds = cameras.map((camera) => [camera.latitude, camera.longitude]);
    map.fitBounds(bounds, { padding: [36, 36], maxZoom: 16 });
  }, [boundsKey, cameras, map]);

  return null;
};

const CameraPopup = ({ camera }) => (
  <Box minW="180px">
    <Text fontWeight="bold" mb={1}>{camera.name}</Text>
    <Text fontSize="sm" color="gray.600">{camera.location || "Sem localização"}</Text>
    <Text fontSize="sm" mt={2}>Passagens: <strong>{formatNumber(camera.passageCount)}</strong></Text>
    <Text fontSize="xs" color="gray.500">Última: {formatDateTime(camera.lastDetectedAt)}</Text>
  </Box>
);

const FlowPopup = ({ flow }) => (
  <Box minW="180px">
    <Text fontWeight="bold" mb={1}>{flow.location}</Text>
    <Text fontSize="sm">Passagens: <strong>{formatNumber(flow.passageCount)}</strong></Text>
    <Text fontSize="xs" color="gray.500">Câmeras: {flow.cameraCount}</Text>
  </Box>
);

const MapTilesLoadingWatcher = ({ onChange }) => {
  const map = useMap();

  useEffect(() => {
    const start = () => onChange(true);
    const finish = () => onChange(false);
    map.on("loading", start);
    map.on("load", finish);
    return () => {
      map.off("loading", start);
      map.off("load", finish);
    };
  }, [map, onChange]);

  return null;
};

const TrafficMap = () => {
  const [mapLayer, setMapLayer] = useState("cameras");
  const [periodHours, setPeriodHours] = useState("1");
  const [hourOfDay, setHourOfDay] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tilesLoading, setTilesLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedCameraIds, setSelectedCameraIds] = useState([]);
  const [cameraSearch, setCameraSearch] = useState("");
  const toast = useToast();

  const fetchMapData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ hours: periodHours });
      if (hourOfDay !== "") params.set("hour", hourOfDay);
      if (vehicleType) params.set("vehicleType", vehicleType);

      const response = await fetch(`/api/maps/traffic?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Erro ao carregar mapa");

      setMapData(data);
      setLastUpdated(new Date());
    } catch (error) {
      toast({
        title: "Erro ao carregar mapa",
        description: error.message,
        status: "error",
        duration: 3500,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  }, [hourOfDay, periodHours, toast, vehicleType]);

  useEffect(() => {
    fetchMapData();
  }, [fetchMapData]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const intervalId = setInterval(fetchMapData, 60000);
    return () => clearInterval(intervalId);
  }, [autoRefresh, fetchMapData]);

  const cameras = useMemo(() => mapData?.cameras || [], [mapData]);
  const cameraDropdownOptions = useMemo(() => {
    if (!cameraSearch.trim()) return cameras;
    const term = cameraSearch.toLowerCase();
    return cameras.filter((camera) => camera.name.toLowerCase().includes(term));
  }, [cameras, cameraSearch]);
  const filteredCameras = useMemo(() => {
    if (selectedCameraIds.length === 0) return cameras;
    return cameras.filter((camera) => selectedCameraIds.includes(String(camera.id)));
  }, [cameras, selectedCameraIds]);

  // Max passage count within the visible camera set — used to normalise colours relatively
  const visibleMax = useMemo(
    () => Math.max(1, ...filteredCameras.map((c) => c.passageCount)),
    [filteredCameras]
  );

  const flows = useMemo(() => {
    const visibleCameraById = new Map(
      filteredCameras.map((camera) => [
        String(camera.id),
        { ...camera, intensity: camera.passageCount / visibleMax }
      ])
    );

    return (mapData?.flows || [])
      .map((flow) => {
        const visibleCameraIds = (flow.cameraIds || [])
          .map(String)
          .filter((id) => visibleCameraById.has(id));

        if (!visibleCameraIds.length) return null;

        const fallbackIntensity = Math.max(
          ...visibleCameraIds.map((id) => visibleCameraById.get(id)?.intensity || 0)
        );
        const segments = (flow.segments || [])
          .map((segment, index) => {
            if (!Array.isArray(segment) || segment.length <= 1) return null;

            const cameraId = String(flow.segmentCameraIds?.[index] ?? flow.cameraIds?.[index] ?? "");
            const camera = visibleCameraById.get(cameraId);
            if (!camera && selectedCameraIds.length > 0) return null;

            return {
              positions: segment,
              intensity: camera?.intensity ?? fallbackIntensity
            };
          })
          .filter(Boolean);

        if (!segments.length) return null;

        const passageCount = visibleCameraIds.reduce((sum, id) => sum + (visibleCameraById.get(id)?.passageCount || 0), 0);
        return {
          ...flow,
          passageCount,
          cameraCount: visibleCameraIds.length,
          segments,
          intensity: fallbackIntensity
        };
      })
      .filter(Boolean);
  }, [filteredCameras, mapData, selectedCameraIds.length, visibleMax]);

  const camerasWithTraffic = useMemo(() => {
    return filteredCameras.filter((camera) => camera.passageCount > 0);
  }, [filteredCameras]);
  const topCamera = useMemo(() => {
    return [...filteredCameras].sort((a, b) => b.passageCount - a.passageCount)[0] || null;
  }, [filteredCameras]);

  const mapCenter = cameras.length ? [cameras[0].latitude, cameras[0].longitude] : [-23.55052, -46.633308];

  return (
    <Box p={6} flex="1" minH="100vh" bg="gray.100">
      <Flex justify="space-between" align={{ base: "start", xl: "center" }} direction={{ base: "column", xl: "row" }} gap={4} mb={5}>
        <Box>
          <Heading size="lg">Mapa de Fluxo Urbano</Heading>
          <Text color="gray.600" fontSize="sm" mt={1}>
            {lastUpdated ? `Atualizado em ${formatDateTime(lastUpdated)}` : "Carregando dados"}
          </Text>
        </Box>

        <HStack spacing={3} flexWrap="wrap" justify={{ base: "flex-start", xl: "flex-end" }}>
          <ButtonGroup isAttached size="sm" variant="outline">
            <Button
              leftIcon={<FaVideo />}
              colorScheme={mapLayer === "cameras" ? "teal" : "gray"}
              variant={mapLayer === "cameras" ? "solid" : "outline"}
              onClick={() => setMapLayer("cameras")}
            >
              Câmeras
            </Button>
            <Button
              leftIcon={<FaFireAlt />}
              colorScheme={mapLayer === "heatmap" ? "orange" : "gray"}
              variant={mapLayer === "heatmap" ? "solid" : "outline"}
              onClick={() => setMapLayer("heatmap")}
            >
              Heatmap
            </Button>
            <Button
              leftIcon={<FaRoute />}
              colorScheme={mapLayer === "flow" ? "blue" : "gray"}
              variant={mapLayer === "flow" ? "solid" : "outline"}
              onClick={() => setMapLayer("flow")}
            >
              Fluxo
            </Button>
          </ButtonGroup>

          <Button leftIcon={<RepeatIcon />} size="sm" colorScheme="teal" variant="outline" onClick={fetchMapData} isLoading={loading}>
            Atualizar
          </Button>
        </HStack>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={5}>
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <Stat>
            <StatLabel>Câmeras no mapa</StatLabel>
            <StatNumber>{formatNumber(mapData?.totals?.cameras)}</StatNumber>
            <StatHelpText>com latitude e longitude</StatHelpText>
          </Stat>
        </Box>
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <Stat>
            <StatLabel>Passagens no período</StatLabel>
            <StatNumber>{formatNumber(mapData?.totals?.totalPassages)}</StatNumber>
            <StatHelpText>{PERIOD_OPTIONS.find((option) => option.value === periodHours)?.label}</StatHelpText>
          </Stat>
        </Box>
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <Stat>
            <StatLabel>Maior fluxo</StatLabel>
            <StatNumber>{formatNumber(mapData?.totals?.maxPassages)}</StatNumber>
            <StatHelpText>{topCamera?.name || "Sem dados"}</StatHelpText>
          </Stat>
        </Box>
        <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
          <Stat>
            <StatLabel>Ruas com fluxo</StatLabel>
            <StatNumber>{formatNumber(flows.length)}</StatNumber>
            <StatHelpText>{autoRefresh ? "tempo real ativo" : "tempo real pausado"}</StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      <Flex bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4} mb={5} gap={4} align="end" wrap="wrap" position="relative" zIndex={500}>
        <FormControl maxW={{ base: "100%", md: "210px" }}>
          <FormLabel>Período</FormLabel>
          <Select value={periodHours} onChange={(event) => setPeriodHours(event.target.value)}>
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl maxW={{ base: "100%", md: "180px" }}>
          <FormLabel>Hora do dia</FormLabel>
          <Select value={hourOfDay} onChange={(event) => setHourOfDay(event.target.value)}>
            <option value="">Todas</option>
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={hour}>{String(hour).padStart(2, "0")}:00</option>
            ))}
          </Select>
        </FormControl>

        <FormControl maxW={{ base: "100%", md: "220px" }}>
          <FormLabel>Tipo de veículo</FormLabel>
          <Select value={vehicleType} onChange={(event) => setVehicleType(event.target.value)}>
            <option value="">Todos</option>
            {VEHICLE_TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl maxW={{ base: "100%", md: "220px" }}>
          <FormLabel>Câmeras</FormLabel>
          <Menu closeOnSelect={false} onClose={() => setCameraSearch("")}>
            <MenuButton as={Button} size="sm" w="100%" rightIcon={<ChevronDownIcon />} variant="outline" textAlign="left" fontWeight="normal">
              {selectedCameraIds.length === 0 ? "Todas" : `${selectedCameraIds.length} selecionada(s)`}
            </MenuButton>
            <MenuList maxH="260px" overflowY="auto" zIndex={1500} p={0}>
              <Box px={2} pt={2} pb={1} position="sticky" top={0} bg="white" zIndex={1} borderBottomWidth="1px" borderColor="gray.100">
                <Input
                  size="sm"
                  placeholder="Buscar câmera..."
                  value={cameraSearch}
                  onChange={(e) => setCameraSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  borderRadius="md"
                  autoComplete="off"
                />
              </Box>
              <MenuOptionGroup type="checkbox" value={selectedCameraIds} onChange={setSelectedCameraIds}>
                {cameraDropdownOptions.map((camera) => (
                  <MenuItemOption key={camera.id} value={String(camera.id)}>
                    {camera.name}
                  </MenuItemOption>
                ))}
                {cameraDropdownOptions.length === 0 && (
                  <Box px={3} py={2} color="gray.400" fontSize="sm">Nenhuma câmera encontrada</Box>
                )}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </FormControl>

        <HStack h="40px" px={3} borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
          <Switch colorScheme="teal" isChecked={autoRefresh} onChange={(event) => setAutoRefresh(event.target.checked)} />
          <Text fontSize="sm" fontWeight="medium">Tempo real</Text>
        </HStack>
      </Flex>

      <Box position="relative" bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" minH="700px" h="calc(100vh - 190px)">
        {(tilesLoading || (loading && !mapData)) && (
          <Flex position="absolute" inset={0} align="center" justify="center" direction="column" bg="whiteAlpha.900" zIndex={1200} gap={3}>
            <Spinner size="xl" color="teal.500" thickness="4px" />
            <Text fontWeight="medium" color="gray.700">Carregando mapa</Text>
          </Flex>
        )}

        {!loading && cameras.length === 0 && (
          <Flex position="absolute" inset={0} direction="column" align="center" justify="center" bg="white" zIndex={10} px={6} textAlign="center">
            <FaMapMarkerAlt size="32px" color="#718096" />
            <Text mt={3} fontWeight="semibold">Nenhuma câmera georreferenciada</Text>
            <Text color="gray.600" fontSize="sm">Cadastre latitude e longitude nas câmeras para exibi-las no mapa.</Text>
          </Flex>
        )}

        <MapContainer center={mapCenter} zoom={13} style={{ width: "100%", height: "100%" }} preferCanvas>
          <MapTilesLoadingWatcher onChange={setTilesLoading} />
          <TileLayer
            key={mapData?.basemapUrl || DEFAULT_BASEMAP_URL}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url={mapData?.basemapUrl || DEFAULT_BASEMAP_URL}
            subdomains="abcd"
            maxZoom={19}
          />
          <FitMapBounds cameras={filteredCameras} />

          {mapLayer === "heatmap" && filteredCameras.map((camera) => {
            const heatIntensity = camera.passageCount / visibleMax;
            return (
            <Circle
              key={`heat-${camera.id}`}
              center={[camera.latitude, camera.longitude]}
              radius={HEATMAP_MIN_RADIUS_METERS + heatIntensity * (HEATMAP_MAX_RADIUS_METERS - HEATMAP_MIN_RADIUS_METERS)}
              pathOptions={{
                color: getTrafficColor(heatIntensity),
                fillColor: getTrafficColor(heatIntensity),
                fillOpacity: camera.passageCount > 0 ? 0.15 + heatIntensity * 0.3 : 0.05,
                opacity: camera.passageCount > 0 ? 0.35 + heatIntensity * 0.45 : 0.15,
                weight: 1
              }}
            >
              <Popup><CameraPopup camera={camera} /></Popup>
            </Circle>
            );
          })}

          {mapLayer === "flow" && <ZoomAwareFlows flows={flows} />}

          {mapLayer === "cameras" && filteredCameras.map((camera) => (
            <Marker
              key={`camera-${camera.id}`}
              position={[camera.latitude, camera.longitude]}
              icon={isCameraOnline(camera.lastDetectedAt) ? CAMERA_ICON_ONLINE : CAMERA_ICON_OFFLINE}
            >
              <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold">{camera.name}</Text>
                  <Text>{formatNumber(camera.passageCount)} passagens</Text>
                </VStack>
              </Tooltip>
              <Popup><CameraPopup camera={camera} /></Popup>
            </Marker>
          ))}

          {mapLayer !== "cameras" && camerasWithTraffic.map((camera) => (
            <CircleMarker
              key={`camera-${camera.id}-${mapLayer}`}
              center={[camera.latitude, camera.longitude]}
              radius={5}
              pathOptions={{
                color: getTrafficColor(camera.passageCount / visibleMax),
                fillColor: getTrafficColor(camera.passageCount / visibleMax),
                fillOpacity: 0.72,
                opacity: 1,
                weight: 2
              }}
            >
              <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold">{camera.name}</Text>
                  <Text>{formatNumber(camera.passageCount)} passagens</Text>
                </VStack>
              </Tooltip>
              <Popup><CameraPopup camera={camera} /></Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </Box>

      <HStack mt={4} spacing={3} flexWrap="wrap">
        <Badge colorScheme="teal" px={3} py={1}>Baixo</Badge>
        <Badge colorScheme="yellow" px={3} py={1}>Médio</Badge>
        <Badge colorScheme="orange" px={3} py={1}>Alto</Badge>
        <Badge colorScheme="red" px={3} py={1}>Crítico</Badge>
      </HStack>
    </Box>
  );
};

export default TrafficMap;